import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { Box, CardContent } from "@mui/material";
import { FC, useState } from "react";

import { useCourseProgress } from "../../hooks/useCourseProgress";
import { ErrorSeverity, useErrorHandler } from "../../hooks/useErrorHandler";
import { MyCoursesQuery_myCourses_rosterUsers } from "../../pages/Library/__generated__/MyCoursesQuery";
import { SetupLink, SetupStep, SetupStepWrapper } from "../../pages/Library/SetupInstructions.elements";
import { toDateAtTimeWithTz } from "../../util/time";
import { LibraryCard } from "./LibraryCard";
import { CourseName, InitContentWrapper, LibraryButton, LibraryCardElement, SmallFont } from "./LibraryCard.elements";

interface LibraryCardProps {
  course: MyCoursesQuery_myCourses_rosterUsers;
}
export const LibraryCardInit: FC<LibraryCardProps> = ({ course }) => {
  const { reportError } = useErrorHandler();
  const { startCourse } = useCourseProgress(course.course.trainingId, course.course.id, course.trainingInstance?.id);
  const [startedCourse, setStartedCourse] = useState<MyCoursesQuery_myCourses_rosterUsers>();
  const [starting, setStarting] = useState(false);
  const start = async () => {
    setStarting(true);
    const {
      errors,
      data: { startCourse: startedCourse },
    } = await startCourse({ variables: { courseId: course.course.trainingId } });
    setStarting(false);
    if (errors) {
      return reportError(ErrorSeverity.LOW, "Failed to start course", { errors: [...errors] });
    } else {
      setStartedCourse(startedCourse);
    }
  };

  return startedCourse ? (
    <LibraryCard course={startedCourse} />
  ) : (
    <LibraryCardElement>
      <CardContent sx={{ width: "100%", padding: "0" }}>
        <Box>
          <CourseName>{course.course.name}</CourseName>
          <InitContentWrapper elevation={0}>
            <LibraryButton variant="contained" disableElevation onClick={() => start()} loading={starting}>
              Start Course
            </LibraryButton>
            <Box>
              <SmallFont>This course will expire 120 days after you start it.</SmallFont>
              <SmallFont>
                If you haven&apos;t started by {toDateAtTimeWithTz(course.registrationDeadline)}, the course will start
                automatically.
              </SmallFont>
            </Box>
            {course.shippingTracking && (
              <SetupStepWrapper>
                <SetupStep>
                  <LocalShippingIcon className="icon" />
                  <div>
                    <SetupLink
                      target="_blank"
                      // eslint-disable-next-line max-len
                      href={`https://www.ups.com/tracking/tracking.cgi?tracknum=${course.shippingTracking?.trackingNumber}`}
                    >
                      Track your course materials shipment
                    </SetupLink>
                    <span>&nbsp;&nbsp;(Status: {course.shippingTracking?.deliveryStatus})</span>
                  </div>
                </SetupStep>
              </SetupStepWrapper>
            )}
          </InitContentWrapper>
        </Box>
      </CardContent>
    </LibraryCardElement>
  );
};
